import React, { useMemo, useRef, useEffect, useState } from 'react'
import './unifiedUI.scss'

/**
 * StepIndicator Component - REBUILT FROM SCRATCH
 * 
 * Two separate components:
 * 1. Phase labels - fixed width (12rem), slide left/right
 * 2. Filmstrip preview - centered, shows current phase content
 */

const StepIndicator = ({
  projectData,
  currentItemIndex,
  currentLevel,
  onNavigate,
  compactMode = false
}) => {
  const phasesContainerRef = useRef(null)
  const activeStepRef = useRef(null)
  const previousPhaseIndex = useRef(-1)
  const inactivityTimerRef = useRef(null)
  const [isContentChanging, setIsContentChanging] = useState(false)
  const [isFilmstripVisible, setIsFilmstripVisible] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Show filmstrip and reset inactivity timer
  const showFilmstrip = () => {
    setIsFilmstripVisible(true)
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }
    
    // Set new timer to hide after 2 seconds
    inactivityTimerRef.current = setTimeout(() => {
      setIsFilmstripVisible(false)
    }, 2000)
  }

  // Flatten project structure
  const projectStructure = useMemo(() => {
    if (!projectData) return null

    const phases = []
    let globalStepIndex = 0

    projectData.sections.forEach((section, sectionIndex) => {
      const phase = {
        id: section.id || `phase-${sectionIndex}`,
        title: isMobile && section.shortTitle 
          ? section.shortTitle 
          : (section.label || section.sectionTitle || section.title || `Phase ${sectionIndex + 1}`),
        sections: []
      }

      section.subsections?.forEach((subsection, subsectionIndex) => {
        const sectionData = {
          id: subsection.id || `section-${subsectionIndex}`,
          title: subsection.title,
          steps: [],
          startIndex: globalStepIndex,
          endIndex: globalStepIndex + subsection.steps.length - 1
        }

        subsection.steps.forEach((step) => {
          sectionData.steps.push({
            globalIndex: globalStepIndex,
            previewImage: step.image, // Always use original image for filmstrip
            type: step.type
          })
          globalStepIndex++
        })

        phase.sections.push(sectionData)
      })

      phase.startIndex = phase.sections[0]?.startIndex ?? 0
      phase.endIndex = phase.sections[phase.sections.length - 1]?.endIndex ?? 0
      phases.push(phase)
    })

    return { phases, totalSteps: globalStepIndex }
  }, [projectData, isMobile])

  // Find current phase
  const currentPhaseIndex = useMemo(() => {
    if (!projectStructure) return 0
    return projectStructure.phases.findIndex(
      phase => currentItemIndex >= phase.startIndex && currentItemIndex <= phase.endIndex
    )
  }, [projectStructure, currentItemIndex])

  // Find current section within current phase
  const currentSectionIndex = useMemo(() => {
    if (!projectStructure || currentPhaseIndex === -1) return 0
    const phase = projectStructure.phases[currentPhaseIndex]
    return phase.sections.findIndex(
      section => currentItemIndex >= section.startIndex && currentItemIndex <= section.endIndex
    )
  }, [projectStructure, currentPhaseIndex, currentItemIndex])

  // Slide phases when current phase changes
  useEffect(() => {
    if (!phasesContainerRef.current) return
    
    // Slide phase labels: -currentPhaseIndex * 12rem
    const offset = -currentPhaseIndex * 12
    phasesContainerRef.current.style.transform = `translateX(${offset}rem)`
    
    // Fade content when phase changes
    if (previousPhaseIndex.current !== -1 && previousPhaseIndex.current !== currentPhaseIndex) {
      setIsContentChanging(true)
      setTimeout(() => setIsContentChanging(false), 600)
    }
    
    previousPhaseIndex.current = currentPhaseIndex
  }, [currentPhaseIndex])

  // Show filmstrip on currentItemIndex change
  useEffect(() => {
    showFilmstrip()
  }, [currentItemIndex])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [])

  if (!projectStructure) return null

  return (
    <div className="step-indicator-container">
      {/* Left arrow */}


      <div className="step-indicator-content">
        {/* Filmstrip - preview images on top */}
        <div className={`filmstrip-container ${isFilmstripVisible ? 'visible' : 'hidden'}`}>
          {/* Compact mode: Show ALL phases in one continuous filmstrip */}
          {compactMode ? (
            <div className="filmstrip-content active">
              {projectStructure.phases.map((phase) => (
                phase.sections.map((section, sectionIndex) => {
                  const isSectionActive = section.steps.some(step => step.globalIndex === currentItemIndex)

                  return (
                    <div key={section.id} className={`filmstrip-section ${isSectionActive ? 'active' : ''}`}>
                      {section.steps.map((step) => {
                        const isCurrentStep = step.globalIndex === currentItemIndex

                        return (
                          <div
                            key={step.globalIndex}
                            ref={isCurrentStep ? activeStepRef : null}
                            className={`filmstrip-step ${isCurrentStep ? 'current' : ''}`}
                            data-step-type={step.type}
                            onClick={() => onNavigate(step.globalIndex)}
                          >
                            <img src={step.previewImage} alt={`Step ${step.globalIndex + 1}`} />
                            {isCurrentStep && (
                              <div className="step-current-indicator"></div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })
              ))}
            </div>
          ) : (
            /* Standard mode: Show only current phase */
            projectStructure.phases.map((phase, phaseIndex) => (
              <div 
                key={phase.id}
                className={`filmstrip-content ${phaseIndex === currentPhaseIndex ? 'active' : 'inactive'}`}
              >
                {phase.sections.map((section, sectionIndex) => {
                  const isSectionActive = sectionIndex === currentSectionIndex && phaseIndex === currentPhaseIndex

                  return (
                    <div key={section.id} className={`filmstrip-section ${isSectionActive ? 'active' : ''}`}>
                      {section.steps.map((step) => {
                        const isCurrentStep = step.globalIndex === currentItemIndex

                        return (
                          <div
                            key={step.globalIndex}
                            ref={isCurrentStep ? activeStepRef : null}
                            className={`filmstrip-step ${isCurrentStep ? 'current' : ''}`}
                            data-step-type={step.type}
                            onClick={() => onNavigate(step.globalIndex)}
                          >
                            <img src={step.previewImage} alt={`Step ${step.globalIndex + 1}`} />
                            {isCurrentStep && (
                              <div className="step-current-indicator"></div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>
        <div className='nav-container'>
          {/* Left spacer for centering */}
          <div className="nav-container-step-indicator"></div>
          
          {/* Phase labels - always centered */}
          <div className="step-indicator">
            {projectStructure.phases.map((phase, index) => (
              <React.Fragment key={phase.id}>
                <button
                  className={`menu-item ${index === currentPhaseIndex ? 'active' : ''}`}
                  onClick={() => onNavigate(phase.startIndex)}
                >
                  {phase.title.toUpperCase()}
                </button>
              </React.Fragment>
            ))}
          </div>
          
          {/* Navigation arrows - right aligned (hidden on mobile) */}
          {!isMobile && (
            <div className='nav-button-set'>
              <button 
                className="nav-btn" 
                onClick={() => onNavigate(Math.max(0, currentItemIndex - 1))}
                disabled={currentItemIndex === 0}
              >
                <span className="material-symbols-rounded">arrow_back</span>
              </button>
              <button 
                className="nav-btn" 
                onClick={() => onNavigate(Math.min(projectStructure.totalSteps - 1, currentItemIndex + 1))}
                disabled={currentItemIndex >= projectStructure.totalSteps - 1}
              >
                <span className="material-symbols-rounded">arrow_forward</span>
              </button>
            </div>
          )}
        </div>
      </div>

     
    </div>
  )
}

export default StepIndicator
